import { createAuthStore } from "@/stores/createAuthStore";
import {
  clinicianLoginApi,
  clinicianRefreshApi,
  clinicianLogoutApi,
} from "@/portals/clinician/api/clinicianAuthApi";
import type { ContractResult } from "@/portals/clinician/api/contractErrors";
import type { ClinicianAuthResponse, ClinicianProfile } from "@/portals/clinician/api/types";

// Its own createAuthStore instance registered under portal "clinician": its own
// in-memory token, refresh timer, and apiClient binding, so it can never read or
// overwrite the patient/admin sessions (see clinicianAuthStore.test.ts, F6).
// The user is read loosely from login/refresh because the contract doesn't name
// the key; ClinicianLayout fetches /api/clinician/me for the displayed name anyway.
function toAuthApiResult({ data, error }: ContractResult<ClinicianAuthResponse>) {
  if (!data) return { data: null, error: error?.message ?? null, code: error?.code ?? null };
  return {
    data: {
      accessToken: data.accessToken,
      expiresIn: data.expiresIn ?? NaN,
      user: data.clinician ?? data.user ?? null,
    },
    error: null,
  };
}

export const useClinicianAuthStore = createAuthStore<ClinicianProfile | null, [email: string, password: string]>({
  portal: "clinician",
  signIn: async (email, password) => {
    const result = await clinicianLoginApi(email, password);
    // Contract: every login 401 is INVALID_CREDENTIALS, whatever code string rides along.
    if (result.error?.status === 401) result.error = { ...result.error, code: "INVALID_CREDENTIALS" };
    return toAuthApiResult(result);
  },
  refresh: async () => toAuthApiResult(await clinicianRefreshApi()),
  signOut: () => clinicianLogoutApi().then(() => {}),
});
